import type { Component } from '../../router';
import { navigate } from '../../router';
import { icon, type IconName } from '../icons';
import { t, getLocale, SUPPORTED_LOCALES, type Locale, type StringKey } from '../../i18n';
import { getStoredTheme, setTheme, type Theme } from '../../utils/theme';
import { currentUserEmail, logout, switchCompany } from '../../services/auth';
import { rbacContext } from '../../services/capabilities';
import { promptCompanyPicker } from '../CompanyPicker';
import { confirmDialog } from '../ConfirmDialog';
import { segmentedToggle } from '../SegmentedToggle';
import { LOCALE_LABEL, switchLocale } from '../LocaleSwitcher';
import { accountMenuItems, isAccountArea, type NavItem } from './navModel';
import type { CompanyMembership } from '../../api/client';

// AccountMenu — the desk chrome's account dropdown (FE-010, spec §5.7). A
// click-anchored disclosure off a right-side top-nav trigger: identity (email +
// active company), the account-settings routes (Devices & sessions, Passkeys),
// in-menu theme + language fast toggles (D14), Switch company, and Sign out — the
// controls that were previously reachable on desktop only by typing /me (§4.2).
//
// Not a role="menu": the panel mixes navigation links, actions, and segmented
// toggle-button groups, so it follows the disclosure pattern (a button with
// aria-expanded controlling a labelled panel) rather than strict menu semantics —
// simpler and axe-clean. Dismissal mirrors contextMenu.ts (Escape, outside
// pointerdown, resize); a route click tears the shell down and rebuilds a fresh,
// closed menu (each route is wrapped in its own AppShell), so links need no
// explicit close.

// Label KEYS, not resolved strings — resolved with t() at render time so the menu
// follows a live language switch (the pitfall MePage's module-level THEMES hit).
const THEME_ORDER: readonly Theme[] = ['auto', 'light', 'dark'];
const THEME_LABEL: Record<Theme, StringKey> = {
  auto: 'me.theme.auto',
  light: 'me.theme.light',
  dark: 'me.theme.dark',
};

export interface AccountMenuPanelOptions {
  id: string;
  email: string | null;
  companyName: string | null;
  routeItems: NavItem[];
  theme: Theme;
  locale: Locale;
  canSwitchCompany: boolean;
  onSwitchCompany: () => void;
  onSelectTheme: (theme: Theme) => void;
  onSelectLocale: (locale: Locale) => void;
  onSignOut: () => void;
  // HQ standing self-visibility (RBAC-012b): true renders the "HQ — all
  // companies" badge in the identity block so a holder sees their own standing;
  // false/undefined renders no trace of it. Fed from rbacContext()?.hqStanding.
  hqStanding?: boolean;
  // The visual catalog renders the panel open, in flow — this drops it into a
  // static layout so the section screenshot has real height (FE-010.5).
  demo?: boolean;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function rowIcon(name: IconName): HTMLElement {
  const box = el('span', 'acct-menu__row-icon');
  box.appendChild(icon(name));
  return box;
}

function rowChevron(): HTMLElement {
  const box = el('span', 'acct-menu__row-chevron');
  box.appendChild(icon('chevron-right'));
  return box;
}

// buildAccountMenuPanel renders the dropdown panel. Pure and data-driven so the
// live menu, the visual catalog, and unit tests all share one source of truth.
export function buildAccountMenuPanel(opts: AccountMenuPanelOptions): HTMLElement {
  const panel = el('div', 'acct-menu__panel');
  panel.id = opts.id;
  if (opts.demo) panel.classList.add('acct-menu__panel--static');

  // Identity — the caller's own email + the active company (FE-010.2), plus the
  // HQ standing badge when the caller holds it (RBAC-012b). role="status" makes
  // it legible to assistive tech as the standout identity fact; a non-holder
  // gets no node at all — no empty badge, no reserved space.
  const identity = el('div', 'acct-menu__identity');
  if (opts.email) identity.appendChild(el('span', 'acct-menu__email', opts.email));
  if (opts.companyName) identity.appendChild(el('span', 'acct-menu__company', opts.companyName));
  if (opts.hqStanding) {
    const badge = el('span', 'acct-menu__hq-badge', t('account.hqBadge'));
    badge.setAttribute('role', 'status');
    identity.appendChild(badge);
  }
  if (identity.childElementCount > 0) panel.appendChild(identity);

  // Account-settings routes (from navModel) + switch company.
  const nav = el('div', 'acct-menu__group');
  for (const item of opts.routeItems) {
    const a = el('a', 'acct-menu__row');
    a.href = item.href;
    if (item.icon) a.appendChild(rowIcon(item.icon));
    a.appendChild(el('span', 'acct-menu__row-label', t(item.labelKey)));
    a.appendChild(rowChevron());
    nav.appendChild(a);
  }
  if (opts.canSwitchCompany) {
    const btn = el('button', 'acct-menu__row');
    btn.type = 'button';
    btn.appendChild(rowIcon('cases'));
    btn.appendChild(el('span', 'acct-menu__row-label', t('me.switchCompany')));
    btn.appendChild(rowChevron());
    btn.addEventListener('click', () => opts.onSwitchCompany());
    nav.appendChild(btn);
  }
  if (nav.childElementCount > 0) panel.appendChild(nav);

  // Appearance + language fast toggles (D14).
  const controls = el('div', 'acct-menu__group');
  const appearance = el('div', 'acct-menu__row acct-menu__row--control');
  appearance.appendChild(rowIcon('appearance'));
  appearance.appendChild(el('span', 'acct-menu__row-label', t('me.appearance')));
  appearance.appendChild(
    segmentedToggle({
      baseClass: 'acct-menu__segment',
      ariaLabel: t('me.theme.aria'),
      values: THEME_ORDER,
      labelFor: (v) => t(THEME_LABEL[v]),
      current: opts.theme,
      onSelect: opts.onSelectTheme,
      live: true,
    }),
  );
  controls.appendChild(appearance);

  const language = el('div', 'acct-menu__row acct-menu__row--control');
  language.appendChild(rowIcon('globe'));
  language.appendChild(el('span', 'acct-menu__row-label', t('me.language')));
  language.appendChild(
    segmentedToggle({
      baseClass: 'acct-menu__segment',
      ariaLabel: t('me.language'),
      values: SUPPORTED_LOCALES,
      labelFor: (v) => t(LOCALE_LABEL[v]),
      current: opts.locale,
      onSelect: opts.onSelectLocale,
      live: false,
    }),
  );
  controls.appendChild(language);
  panel.appendChild(controls);

  // Sign out.
  const danger = el('div', 'acct-menu__group');
  const signOut = el('button', 'acct-menu__row acct-menu__row--danger');
  signOut.type = 'button';
  signOut.appendChild(rowIcon('sign-out'));
  signOut.appendChild(el('span', 'acct-menu__row-label', t('common.signOut')));
  signOut.addEventListener('click', () => opts.onSignOut());
  danger.appendChild(signOut);
  panel.appendChild(danger);

  return panel;
}

export class AccountMenu implements Component {
  private static seq = 0;
  private readonly panelId = `account-menu-panel-${(AccountMenu.seq += 1)}`;
  private root: HTMLElement | null = null;
  private trigger: HTMLButtonElement | null = null;
  private panel: HTMLElement | null = null;
  private open = false;
  private dismiss: (() => void) | null = null;

  mount(host: HTMLElement): void {
    const wrap = el('div', 'acct-menu');
    const trigger = el('button', 'acct-menu__trigger');
    trigger.type = 'button';
    trigger.setAttribute('aria-haspopup', 'true');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-label', t('me.menu.aria'));
    // Light the trigger while the user is on one of its routes (/me, /settings/*)
    // — the desk mirror of the tab bar's Me prefix rule, single-sourced in navModel.
    if (isAccountArea()) trigger.setAttribute('aria-current', 'page');
    trigger.appendChild(icon('me'));
    const caret = icon('chevron-down');
    caret.classList.add('acct-menu__caret');
    trigger.appendChild(caret);
    trigger.addEventListener('click', () => this.toggle());

    wrap.appendChild(trigger);
    host.appendChild(wrap);
    this.root = wrap;
    this.trigger = trigger;
  }

  private toggle(): void {
    if (this.open) this.close();
    else this.openPanel();
  }

  private openPanel(): void {
    if (this.open || !this.root || !this.trigger) return;
    const ctx = rbacContext();
    const companies = ctx?.companies ?? [];
    const activeId = ctx?.activeCompanyId ?? null;
    const active = companies.find((c) => c.companyId === activeId) ?? null;

    const panel = buildAccountMenuPanel({
      id: this.panelId,
      email: currentUserEmail(),
      companyName: active?.name ?? null,
      hqStanding: ctx?.hqStanding ?? false,
      routeItems: accountMenuItems(),
      theme: getStoredTheme(),
      locale: getLocale(),
      canSwitchCompany: companies.length > 1,
      onSwitchCompany: () => {
        this.close();
        void this.promptSwitch(companies, activeId);
      },
      onSelectTheme: (theme) => setTheme(theme),
      onSelectLocale: (locale) => {
        this.close();
        // The canonical switch (LocaleSwitcher.switchLocale): the lazy es/pt
        // chunk loads first, then the persisted change broadcasts
        // LOCALE_CHANGED_EVENT and app.ts remounts the shell in the new locale,
        // no reload (FE-010.3).
        void switchLocale(locale);
      },
      onSignOut: () => {
        this.close();
        void this.confirmSignOut();
      },
    });

    this.root.appendChild(panel);
    this.panel = panel;
    this.open = true;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.trigger.setAttribute('aria-controls', this.panelId);
    this.wireDismiss();
    panel.querySelector<HTMLElement>('a[href], button:not([disabled])')?.focus();
  }

  private close(): void {
    if (!this.open) return;
    this.open = false;
    this.dismiss?.();
    this.dismiss = null;
    this.panel?.remove();
    this.panel = null;
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.trigger?.removeAttribute('aria-controls');
  }

  private wireDismiss(): void {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const trigger = this.trigger;
        this.close();
        trigger?.focus();
      }
    };
    const onOutside = (e: Event): void => {
      if (!this.root?.contains(e.target as Node)) this.close();
    };
    const onResize = (): void => this.close();
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);
    // Defer outside-dismiss to the next tick so the click that opened the menu
    // doesn't immediately close it (contextMenu.ts uses the same guard).
    const wire = setTimeout(() => {
      document.addEventListener('pointerdown', onOutside, true);
    }, 0);
    this.dismiss = () => {
      clearTimeout(wire);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('pointerdown', onOutside, true);
    };
  }

  private async promptSwitch(
    companies: CompanyMembership[],
    activeId: string | null,
  ): Promise<void> {
    const chosen = await promptCompanyPicker(companies, {
      title: t('me.switchCompany'),
      message: t('me.switchCompanyMsg'),
      cancelable: true,
      activeCompanyId: activeId ?? undefined,
    });
    if (!chosen || chosen === activeId) return;
    try {
      // switchCompany broadcasts RBAC_CONTEXT_EVENT → TopNav.refresh() rebuilds
      // this menu with the new company, so there is nothing to update here.
      await switchCompany(chosen);
    } catch {
      // Keep the current company if the switch is rejected.
    }
  }

  private async confirmSignOut(): Promise<void> {
    const ok = await confirmDialog({
      title: t('me.signOutConfirm.title'),
      message: t('me.signOutConfirm.message'),
      confirmLabel: t('common.signOut'),
      cancelLabel: t('common.cancel'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await logout();
    } catch {
      // ignore — logout() clears local auth state even when the network call fails
    } finally {
      // replace: the page they just signed out of must not be one Back press
      // away — it would only bounce off the guard back to /login (issue 295).
      navigate('/login', { replace: true });
    }
  }

  unmount(): void {
    this.close();
    this.root?.remove();
    this.root = null;
    this.trigger = null;
  }
}
